import { RootState } from '../reducers/index';
import { Role, Permission } from '../../types/models/roles-permissions';

export const getPrivileges = (state: RootState) => state.privileges;
export const getUserRoles = (state: RootState) => getPrivileges(state)?.userRoles || [];
export const getUserPermissions = (state: RootState) => getPrivileges(state)?.permissions || [];
export const getUserRoleType = (state: RootState) => getPrivileges(state)?.isInternal || false;

export const hasRole = (role: Role) => {
  return (state: RootState) => {
    const roles = getUserRoles(state) || [];
    if (roles.length === 0) {
      return false;
    }
    return roles.includes(role);
  };
};

export const hasInternalRole = () => {
  return (state: RootState) => getUserRoleType(state);
};

export const isCustomerServiceRepresentative = () => {
  return hasRole(Role.CUSTOMER_SERVICE_REPRESENTATIVE);
};

export const isCustomerServiceDispatcher = () => {
  return hasRole(Role.CUSTOMER_SERVICE_DISPATCHER);
};
export const isScholarShipsProgramAdmin = () => {
  return hasRole(Role.SCHOLARSHIPS_PROGRAM_ADMIN);
};
export const isCareerServicesProgramAdmin = () => {
  return hasRole(Role.CAREER_SERVICES_PROGRAM_ADMIN);
};
export const isLegalExecutive = () => {
  return hasRole(Role.LEGAL_EXECUTIVE);
};
export const isProgramExecutive = () => {
  return hasRole(Role.PROGRAM_EXECUTIVE);
};
export const isEventManager = () => {
  return hasRole(Role.EVENT_MANAGER);
};
export const isMentorMatchCarrerCoachProgramAdmin = () => {
  return hasRole(Role.MENTOR_MATCH_CAREER_COACH_PROGRAM_ADMIN);
};
export const isCommunitiesProgramAdmin = () => {
  return hasRole(Role.COMMUNITIES_PROGRAM_ADMIN);
};
export const isScholarAdvocate = () => {
  return hasRole(Role.SCHOLAR_ADVOCATE);
};
export const isFinancialAdministrator = () => {
  return hasRole(Role.FINANCIAL_ADMINISTRATOR);
};
export const isDisbursementManager = () => {
  return hasRole(Role.DISBURSEMENT_MANAGER);
};
export const isAccountAdministrator = () => {
  return hasRole(Role.ACCOUNTING_ADMINISTRATOR);
};
export const isCommunicationsManager = () => {
  return hasRole(Role.COMMUNICATIONS_MANAGER);
};
export const isReaderManagement = () => {
  return hasRole(Role.READER_MANAGEMENT);
};
export const isInterviewManagement = () => {
  return hasRole(Role.INTERVIEW_MANAGEMENT);
};
export const isDocumentVerificationTechnician = () => {
  return hasRole(Role.DOCUMENT_VERIFICATION_TECHNICIAN);
};
export const isDocumentVerificationManager = () => {
  return hasRole(Role.DOCUMENT_VERIFICATION_MANAGER);
};
export const isUserAccountManagement = () => {
  return hasRole(Role.USER_ACCOUNT_MANAGEMENT);
};
export const isSystemAdministrator = () => {
  return hasRole(Role.SYSTEM_ADMINISTRATOR);
};

export const hasPermission = (permission: Permission) => {
  return (state: RootState) => {
    const permissions = getUserPermissions(state) || [];
    if (permissions.length === 0) {
      return false;
    }
    return permissions.includes(permission);
  };
};

export const hasMultiplePermissions = (permissionsList: Permission[]) => {
  return (state: RootState) => {
    const permissions = getUserPermissions(state) || [];
    if (permissions.length === 0) {
      return false;
    }
    return permissionsList.every((item) => permissions.includes(item));
  };
};

export const hasOneOfPermissions = (permissionsList: Permission[]) => {
  return (state: RootState) => {
    const permissions = getUserPermissions(state) || [];
    if (permissions.length === 0) {
      return false;
    }
    return permissionsList.some((item) => permissions.includes(item));
  };
};

export const hasFinancialPortalPermission = () => {
  return hasOneOfPermissions([
    Permission.CAN_VIEW_FST_FUNDING_REQUEST,
    Permission.CAN_APPROVE_FST_FUNDING_REQUEST,
    Permission.CAN_VIEW_SND_FUNDING_REQUEST,
    Permission.CAN_APPROVE_SND_FUNDING_REQUEST,
    Permission.CAN_VIEW_SCHOLAR_FUNDING_STATUS_ADMINISTER_ENROLLMENT_VERIFICATION,
    Permission.CAN_UPDATE_SCHOLAR_FUNDING_STATUS_ADMINISTER_ENROLLMENT_VERIFICATION,
    Permission.CAN_DISBURSE_FUNDS,
    Permission.CAN_VIEW_FUNDS,
    Permission.CAN_VIEW_SCHOLARS,
    Permission.CAN_MANAGE_SCHOLARS,
    Permission.CAN_VIEW_SCHOLAR_UNIVERSITY,
    Permission.CAN_MANAGE_SCHOLAR_UNIVERSITY,
    Permission.CAN_VIEW_DISBURSEMENT_ACADEMIC_YEARS,
    Permission.CAN_MANAGE_DISBURSEMENT_ACADEMIC_YEARS,
    Permission.CAN_ONBOARD_FINANCIAL_AID_OFFICER,
    Permission.CAN_MANAGE_SCHOLAR_DEFERMENT_ACCESS,
    Permission.CAN_MANAGE_ONBOARDING_SCHOLAR_UNIVERSITY_FOR_DISBURSEMENT,
    Permission.MANAGE_SCHOLAR_TRANSFER_REQUEST,
    Permission.CAN_UPLOAD_REUPLOAD_RESOURCES_FOR_SCHOLAR_AND_FAO,
    Permission.MANAGE_JOURNAL_ENTRY,
  ]);
};

export const hasViewFullProgramManagement = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_ALL_PROGRAMS, Permission.CAN_VIEW_ALL_STAGES]);
};

export const hasManageMessageCenter = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_MESSAGE_CENTER_MESSAGES,
    Permission.CAN_EDIT_MESSAGE_CENTER_MESSAGES,
  ]);
};

export const hasViewPrograms = () => {
  return hasOneOfPermissions([
    Permission.CAN_VIEW_SCHOLARSHIP_PROGRAMS,
    Permission.CAN_VIEW_SURVEY_PROGRAMS,
    Permission.CAN_VIEW_JOB_CAMPAIGN_PROGRAMS,
    Permission.CAN_VIEW_PRIVATE_EVENT_PROGRAMS,
    Permission.CAN_VIEW_ALL_PROGRAMS,
  ]);
};

export const hasCreatePrograms = () => {
  return hasOneOfPermissions([
    Permission.CAN_CREATE_SCHOLARSHIP_PROGRAMS,
    Permission.CAN_CREATE_SURVEY_PROGRAMS,
    Permission.CAN_CREATE_JOB_CAMPAIGN_PROGRAMS,
    Permission.CAN_CREATE_PRIVATE_EVENT_PROGRAMS,
    Permission.CAN_CREATE_EVENTS,
  ]);
};

export const hasDocumentVerification = () => {
  return hasOneOfPermissions([Permission.CAN_REVIEW_DOCUMENT_VERIFICATIONS, Permission.CAN_VIEW_NOT_STARTED_DOCUMENTS]);
};

export const hasManageScholarShipProgram = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_SCHOLARSHIP_PROGRAMS,
    Permission.CAN_CREATE_SCHOLARSHIP_PROGRAMS,
    Permission.CAN_EDIT_SCHOLARSHIP_PROGRAMS,
    Permission.CAN_DELETE_SCHOLARSHIP_PROGRAMS,
  ]);
};

export const hasManageSurveyProgram = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_SURVEY_PROGRAMS,
    Permission.CAN_CREATE_SURVEY_PROGRAMS,
    Permission.CAN_EDIT_SURVEY_PROGRAMS,
    Permission.CAN_DELETE_SURVEY_PROGRAMS,
  ]);
};

export const hasManageJobCampaignProgram = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_JOB_CAMPAIGN_PROGRAMS,
    Permission.CAN_CREATE_JOB_CAMPAIGN_PROGRAMS,
    Permission.CAN_EDIT_JOB_CAMPAIGN_PROGRAMS,
    Permission.CAN_DELETE_JOB_CAMPAIGN_PROGRAMS,
  ]);
};

export const hasManagePrivateEventProgram = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_PRIVATE_EVENT_PROGRAMS,
    Permission.CAN_CREATE_PRIVATE_EVENT_PROGRAMS,
    Permission.CAN_EDIT_PRIVATE_EVENT_PROGRAMS,
    Permission.CAN_DELETE_PRIVATE_EVENT_PROGRAMS,
  ]);
};

export const hasManageDeleteSubmissionFromProgram = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_ALL_STAGES,
    Permission.CAN_VIEW_ALL_SUBMISSIONS,
    Permission.CAN_DELETE_SUBMISSIONS,
  ]);
};

export const hasManagementReader = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_ALL_STAGES,
    Permission.CAN_VIEW_USER_DETAIL,
    Permission.CAN_ASSIGN_READERS,
  ]);
};

export const hasManageFlaggedEssays = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_ALL_STAGES,
    Permission.CAN_FLAG_ESSAYS,
  ]);
};

export const hasManageInterviewer = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_ALL_STAGES,
    Permission.CAN_VIEW_USER_DETAIL,
  ]);
};

export const hasManuallyAssignInterviewer = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_ALL_STAGES,
    Permission.CAN_ASSIGN_INTERVIEWERS,
  ]);
};

export const hasManageEvent = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_EVENTS,
    Permission.CAN_CREATE_EVENTS,
    Permission.CAN_EDIT_EVENTS,
  ]);
};

export const hasReviewDocVerification = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_PROGRAMS,
    Permission.CAN_VIEW_ALL_STAGES,
    Permission.CAN_VIEW_ALL_DOCUMENTS,
    Permission.CAN_REVIEW_DOCUMENT_VERIFICATIONS,
  ]);
  // hasManageMentor: userPermissions.includes() - TBD
};

export const hasAssignMessages = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_MESSAGE_CENTER_MESSAGES,
    Permission.CAN_ASSIGN_MESSAGE_CENTER_MESSAGES,
  ]);
};

export const hasViewBroadcasts = () => {
  return hasPermission(Permission.CAN_VIEW_BROADCASTS);
};

export const hasMassageCenterView = () => {
  return hasPermission(Permission.CAN_VIEW_MESSAGE_CENTER_MESSAGES);
};

export const hasMessageBroadcastCenter = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_BROADCASTS,
    Permission.CAN_CREATE_BROADCASTS,
    Permission.CAN_EDIT_BROADCASTS,
  ]);
};

export const hasViewUserAccounts = () => {
  return hasPermission(Permission.CAN_VIEW_ALL_USER_ACCOUNTS);
};

export const hasManageUserAccounts = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_USER_ACCOUNTS,
    Permission.CAN_CREATE_USER_ACCOUNT,
    Permission.CAN_EDIT_USER_ACCOUNT,
    Permission.CAN_EDIT_USER_ACCOUNT,
    Permission.CAN_EDIT_ROLES_FOR_USER_ACCOUNT,
    Permission.CAN_ACTIVATE_DEACTIVATE_USER_ACCOUNT,
  ]);
};

export const hasDeleteUserAccounts = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_ALL_USER_ACCOUNTS, Permission.CAN_DELETE_USER_ACCOUNT]);
};

export const hasFlagUsers = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_ALL_USER_ACCOUNTS, Permission.CAN_FLAG_USER_ACCOUNT]);
};

export const hasOverrideEmailVerification = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_ALL_USER_ACCOUNTS,
    Permission.CAN_OVERRIDE_EMAIL_VERIFICATION_USER_ACCOUNT,
  ]);
};

export const hasManageContactLog = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_ALL_USER_ACCOUNTS, Permission.CAN_EDIT_USER_CONTACT_LOG_NOTE]);
};

export const hasManageMentorMatch = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_MENTOR_MATCHES,
    Permission.CAN_MANUALLY_MAKE_MENTOR_MATCH,
    Permission.CAN_UNMATCH_MENTORS,
    Permission.CAN_EDIT_MENTOR_MATCH,
  ]);
};

export const hasViewCommunityAndPosts = () => {
  return hasPermission(Permission.CAN_VIEW_COMMUNITIES_AND_POSTS);
};

export const hasCreateComunity = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_COMMUNITIES_AND_POSTS, Permission.CAN_CREATE_COMMUNITIES]);
};

export const hasManagePost = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_COMMUNITIES_AND_POSTS,
    Permission.CAN_REVIEW_AND_APPROVE_FLAGGED_POSTS,
  ]);
};

export const hasBanCommunityUser = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_COMMUNITIES_AND_POSTS, Permission.CAN_BAN_USER_IN_COMMUNITY]);
};

export const hasManageFstApprovalFundingRequest = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_COMMUNITIES_AND_POSTS,
    Permission.CAN_APPROVE_FST_FUNDING_REQUEST,
  ]);
};

export const hasManageSndApprovalFundingRequest = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_SND_FUNDING_REQUEST, Permission.CAN_APPROVE_SND_FUNDING_REQUEST]);
};

export const hasManageScholarFundingStatus = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_SCHOLAR_FUNDING_STATUS,
    Permission.CAN_UPDATE_SCHOLAR_FUNDING_STATUS,
  ]);
};

export const hasManageDisburseFunds = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_FUNDS, Permission.CAN_DISBURSE_FUNDS]);
};

export const hasManageScholarAndFinancialUniversity = () => {
  return hasMultiplePermissions([
    Permission.CAN_VIEW_SCHOLARS,
    Permission.CAN_MANAGE_SCHOLARS,
    Permission.CAN_VIEW_SCHOLAR_UNIVERSITY,
    Permission.CAN_MANAGE_SCHOLAR_UNIVERSITY,
  ]);
};

export const hasManageUniversity = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_SCHOLAR_UNIVERSITY, Permission.CAN_MANAGE_SCHOLAR_UNIVERSITY]);
};

export const hasViewScholarWellnessReports = () => {
  return hasPermission(Permission.CAN_VIEW_WELLNESS_REPORTS);
};

export const hasManageRolesAndPermissions = () => {
  return hasMultiplePermissions([Permission.CAN_VIEW_ROLES, Permission.CAN_EDIT_ROLES, Permission.CAN_CREATE_ROLES]);
};

export const hasViewSelectionStatuses = () => {
  return hasPermission(Permission.CAN_VIEW_SELECTION_STATUSES);
};

export const hasWriteBySelectionStatuses = () => {
  return hasPermission(Permission.CAN_WRITE_BY_SELECTION_STATUSES);
};
